import type { ReactNode } from 'react'
import styles from './feature-card-grid.css?url'

export const featureCardGridLinks = () => [{ rel: 'stylesheet', href: styles }]

type FeatureCard = {
  title: string
  description: ReactNode
  image: string
  icon?: string
  linkUrl?: string
  mediaClassName?: string
}

interface FeatureCardGridProps {
  cards: FeatureCard[]
  columns?: 2 | 3
  headingAs?: 'h3' | 'h4'
  headingSize?: 's' | 'm'
  imageAltSuffix?: string
  frameIcons?: boolean
  linkLabelPrefix?: string
  showLinkIcon?: boolean
}

export const FeatureCardGrid = ({
  cards,
  columns = 2,
  headingAs: Heading = 'h3',
  headingSize = 'm',
  frameIcons = true,
  imageAltSuffix = 'screenshot',
  linkLabelPrefix = 'Learn more about',
  showLinkIcon = false,
}: FeatureCardGridProps) => {
  return (
    <div className="feature-card-grid" data-columns={columns}>
      {cards.map((card, index) => {
        const { title, description, icon, image, linkUrl, mediaClassName } =
          card
        const Copy = (
          <>
            <div className="feature-card-heading">
              {icon && frameIcons && (
                <span className="feature-card-icon-frame">
                  <img
                    alt=""
                    className="feature-card-icon"
                    loading="lazy"
                    src={icon}
                  />
                </span>
              )}

              {icon && !frameIcons && (
                <img
                  alt=""
                  className="feature-card-icon"
                  loading="lazy"
                  src={icon}
                />
              )}

              <div className="feature-card-title-copy">
                <Heading
                  className={`heading-${headingSize} feature-card-title`}
                >
                  {title}
                  {showLinkIcon && linkUrl && (
                    <svg
                      aria-hidden="true"
                      className="feature-card-title-icon"
                      fill="none"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        fill="var(--color-brand-primary-next)"
                        height="19.2391"
                        rx="9.61957"
                        width="19.2391"
                      />
                      <path
                        d="M8.28223 6.12598L11.6412 9.48487L8.28223 12.8438"
                        stroke="var(--color-text-inverse-next)"
                        strokeLinecap="round"
                        strokeWidth="1.14258"
                      />
                    </svg>
                  )}
                </Heading>

                <p className="body-m feature-card-description">{description}</p>
              </div>
            </div>
          </>
        )

        return (
          <article key={`feature-card-${index}`} className="feature-card">
            {linkUrl ? (
              <a
                aria-label={`${linkLabelPrefix} ${title}`}
                className="feature-card-copy"
                href={linkUrl}
                rel="noreferrer"
                target="_blank"
              >
                {Copy}
              </a>
            ) : (
              <div className="feature-card-copy">{Copy}</div>
            )}

            <figure
              className={`feature-card-media ${mediaClassName ?? ''}`.trim()}
            >
              <img
                alt={`${title} ${imageAltSuffix}`}
                className="feature-card-image"
                loading="lazy"
                src={image}
              />
            </figure>
          </article>
        )
      })}
    </div>
  )
}
